import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { BarChart, Percent, TrendingUp, CalendarCheck, Banknote, TrendingDown, UserPlus, Users, Clock, Gauge, CalendarClock, RefreshCw } from 'lucide-react';
import { currencySymbol as signOf, sharedDoctorCurrencySymbol } from '../../utils/currency';

const pct = (num, den) => (den > 0 ? Math.round((num / den) * 100) : 0);
const money = (n, sym) => `${sym}${Number(n || 0).toLocaleString()}`;

// Platform-wide KPI grid for the admin dashboard. All values are derived from the
// admin snapshot already on the wire (booking counts + earnings + doctor/patient
// counts) — no extra backend call.
const SuperadminAnalytics = ({ booking, earnings, counts, leaderboard, currency, currencySymbol: signProp, loading }) => {
  const { t } = useTranslation('dashboard');
  // The sign: an explicit one, else the hospital's `currency`, else the one
  // currency all the leaderboard's doctors share. It used to default to the euro sign,
  // so an Indian hospital's revenue read in euros (P24).
  const currencySymbol = signProp
    ?? (currency ? signOf(currency) : sharedDoctorCurrencySymbol(leaderboard?.data));

  const total = booking?.total || 0;
  const completed = booking?.counts?.Completed || 0;
  const noShow = booking?.counts?.NoShow || 0;
  const canceled = booking?.counts?.Canceled || 0;
  const rescheduled = booking?.rescheduled || 0;
  const totalDoctors = counts?.totalDoctors || 0;
  const revenue = earnings?.totalEarnings || 0;
  const noShowRevenue = booking?.noShowRevenue || 0;
  const newDoctors = counts?.newDoctors || 0;
  const newPatients = counts?.newPatients || 0;
  // Practice-wide EHR (revenue ÷ completed hours across all doctors) + doctors with
  // ≥1 booking in the period, from the leaderboard payload.
  const teamEhr = leaderboard?.teamEhr || 0;
  const teamOccupancy = leaderboard?.teamOccupancy || 0;
  // Upcoming = future booked appointments (booking status 'Booked').
  const upcoming = booking?.counts?.Booked || 0;

  // Only the *derived* metrics — the raw counts (bookings, completed, no-show,
  // canceled, doctors, patients) are already shown in the cards at the top.
  const cards = [
    { id: 'completionRate', label: t('dashboard.completion_rate', 'Completion rate'), value: `${pct(completed, total)}%`, icon: Percent, color: 'from-emerald-400 to-emerald-500' },
    { id: 'noShowRate', label: t('dashboard.no_show_rate', 'No-show rate'), value: `${pct(noShow, total)}%`, icon: Percent, color: 'from-amber-400 to-amber-500' },
    { id: 'cancellationRate', label: t('dashboard.cancellation_rate', 'Cancellation rate'), value: `${pct(canceled, total)}%`, icon: Percent, color: 'from-rose-400 to-rose-500' },
    { id: 'rescheduleRate', label: t('dashboard.reschedule_rate', 'Reschedule rate'), value: `${pct(rescheduled, total)}%`, icon: RefreshCw, color: 'from-orange-400 to-amber-500' },
    { id: 'upcoming', label: t('dashboard.upcoming_bookings', 'Upcoming bookings'), value: upcoming.toLocaleString(), icon: CalendarClock, color: 'from-sky-400 to-blue-500' },
    { id: 'revenue', label: t('dashboard.period_revenue', 'Period revenue'), value: money(revenue, currencySymbol), icon: Banknote, color: 'from-[#4ABDBD] to-[#3A9B9B]' },
    { id: 'revPerAppt', label: t('dashboard.avg_revenue_per_appointment', 'Avg revenue / appt'), value: money(completed > 0 ? Math.round(revenue / completed) : 0, currencySymbol), icon: CalendarCheck, color: 'from-cyan-400 to-cyan-500' },
    { id: 'revPerDoctor', label: t('dashboard.avg_revenue_per_doctor', 'Avg revenue / doctor'), value: money(totalDoctors > 0 ? Math.round(revenue / totalDoctors) : 0, currencySymbol), icon: TrendingUp, color: 'from-blue-400 to-blue-500' },
    { id: 'teamEhr', label: t('dashboard.team_avg_ehr', 'Team avg EHR'), value: `${money(teamEhr, currencySymbol)}/h`, icon: Clock, color: 'from-[#4ABDBD] to-[#2D8A8A]' },
    { id: 'avgOccupancy', label: t('dashboard.avg_occupancy', 'Avg occupancy'), value: `${teamOccupancy}%`, icon: Gauge, color: 'from-violet-400 to-purple-500' },
    { id: 'noShowLost', label: t('dashboard.no_show_revenue_lost', 'No-show revenue lost'), value: money(noShowRevenue, currencySymbol), icon: TrendingDown, color: 'from-rose-400 to-rose-500' },
    { id: 'newDoctors', label: t('dashboard.new_doctors', 'New doctors'), value: newDoctors.toLocaleString(), icon: UserPlus, color: 'from-indigo-400 to-indigo-500' },
    { id: 'newPatients', label: t('dashboard.new_patients', 'New patients'), value: newPatients.toLocaleString(), icon: Users, color: 'from-purple-400 to-purple-500' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white w-full rounded-xl p-4 border border-gray-200/50 shadow-md"
      data-testid="admin-kpi-grid"
    >
      <h3 className="text-base sm:text-lg font-bold text-gray-900 flex items-center mb-3">
        <BarChart className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-[#4ABDBD]" />
        {t('dashboard.key_metrics', 'Key Metrics')}
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        {cards.map((c) => (
          <div
            key={c.id}
            data-testid={`admin-kpi-${c.id}`}
            className="bg-gray-50 rounded-lg p-3 border border-gray-100 flex items-center gap-2"
          >
            <div className={`p-1.5 rounded-md bg-gradient-to-br ${c.color} shrink-0`}>
              <c.icon className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-base font-bold text-gray-900 leading-tight">{loading ? '—' : c.value}</p>
              <p className="text-[11px] text-gray-500 truncate">{c.label}</p>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default SuperadminAnalytics;
